import { Divider, Select, Radio } from "antd";
import Title from "antd/es/typography/Title";
import { useFormikContext } from "formik";
import React, { FC, useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { selectFiltering } from "shared/helpers";
import { Colors } from "shared/themes";
import { AdaptedValues, ExecuteReportValues } from "shared/types";
import {
  useGetMaterialCategoriesWithNoParamsQuery,
  useGetAllMaterialsNoPaginationQuery,
} from "shared/api";
import type { MaterialCategoriesItem } from "shared/api";

interface QualityAdhesionFilterProps {
  adaptedOrders: AdaptedValues[];
  selectedOrderId: number | null;
  onOrderChange: (value: number | null) => void;
  isLoadingOrders: boolean;
}

type OptionType = "material" | "order";

export const QualityAdhesionFilter: FC<QualityAdhesionFilterProps> = ({
  adaptedOrders,
  selectedOrderId,
  onOrderChange,
  isLoadingOrders,
}) => {
  const { t } = useTranslation("", { keyPrefix: "executeReports" });
  const { values, setFieldValue } = useFormikContext<ExecuteReportValues>();

  const [selectedOption, setSelectedOption] = useState<OptionType>("material");
  const [selectedMaterialCategoryId, setSelectedMaterialCategoryId] = useState<
    number | null
  >(null);

  const { data: materialCategories, isLoading: isLoadingCategories } =
    useGetMaterialCategoriesWithNoParamsQuery(undefined);

  const { data: materials, isLoading: isLoadingMaterials } =
    useGetAllMaterialsNoPaginationQuery(
      selectedMaterialCategoryId
        ? { material_category: selectedMaterialCategoryId }
        : undefined,
      { skip: !selectedMaterialCategoryId }
    );

  const adaptedMaterialCategories = useMemo(() => {
    return (materialCategories ?? []).map((cat: MaterialCategoriesItem) => ({
      label: cat.name,
      value: cat.id,
    }));
  }, [materialCategories]);

  const adaptedMaterials = useMemo(() => {
    return (materials ?? []).map((material) => ({
      label: material.name,
      value: material.id,
    }));
  }, [materials]);

  const selectedMaterialCategory = useMemo(() => {
    return materialCategories?.find(
      (cat: MaterialCategoriesItem) => cat.id === selectedMaterialCategoryId
    );
  }, [materialCategories, selectedMaterialCategoryId]);

  const dynamicFieldOptions = useMemo(() => {
    if (!selectedMaterialCategory?.config_dynamic_tracker_fields) {
      return [];
    }

    const options: { label: string; value: string }[] = [];
    selectedMaterialCategory.config_dynamic_tracker_fields.forEach((config) => {
      config.fields.forEach((field) => {
        const fieldValue = `${config.title}.${field.name}`;
        options.push({
          label: fieldValue,
          value: fieldValue,
        });
      });
    });

    return options;
  }, [selectedMaterialCategory]);

  const handleOptionChange = (option: OptionType) => {
    setSelectedOption(option);
    if (option === "material") {
      setFieldValue("order_id", null);
      onOrderChange(null);
    } else {
      setFieldValue("material_id", null);
    }
    setFieldValue("dynamic_tracker_fields", []);
  };

  const handleMaterialCategoryChange = (value: number) => {
    setSelectedMaterialCategoryId(value);
    setFieldValue("material_category_id", value);
    setFieldValue("material_id", null);
    setFieldValue("dynamic_tracker_fields", []);
  };

  const handleMaterialChange = (value: number) => {
    setFieldValue("material_id", value);
  };

  const handleOrderChange = (value: number) => {
    onOrderChange(value);
    setFieldValue("order_id", value);
  };

  const handleDynamicFieldsChange = (selectedFields: string[]) => {
    setFieldValue("dynamic_tracker_fields", selectedFields);
  };

  useEffect(() => {
    if (selectedMaterialCategoryId && selectedMaterialCategory) {
      setFieldValue("material_category_id", selectedMaterialCategoryId);
    }
  }, [selectedMaterialCategoryId, selectedMaterialCategory, setFieldValue]);

  return (
    <>
      <Divider className="my-1.5" style={{ background: Colors.blue.primary }} />

      <div className="flex flex-col gap-y-3">
        <div className="flex gap-x-4">
          <Radio.Group
            value={selectedOption}
            onChange={(e) => handleOptionChange(e.target.value)}
          >
            <Radio value="material">
              Option A: Material Category → Material
            </Radio>
            <Radio value="order">Option B: Order</Radio>
          </Radio.Group>
        </div>

        {selectedOption === "order" ? (
          <>
            <div className="flex gap-x-2 items-center">
              <Title level={5} className="!w-[150px] !m-0 !text-start">
                Material Category
              </Title>
              {" - "}
              <Select
                loading={isLoadingCategories}
                value={selectedMaterialCategoryId}
                placeholder="Select Material Category"
                options={adaptedMaterialCategories}
                onChange={handleMaterialCategoryChange}
                className="w-[300px]"
                size="small"
                showSearch
                filterOption={(input, option) => selectFiltering(input, option)}
              />
            </div>

            {selectedMaterialCategoryId && (
              <div className="flex gap-x-2 items-center">
                <Title level={5} className="!w-[150px] !m-0 !text-start">
                  {t("selectOrder")}
                </Title>
                {" - "}
                <Select
                  loading={isLoadingOrders}
                  value={selectedOrderId}
                  placeholder={t("selectOrder")}
                  options={adaptedOrders}
                  onChange={handleOrderChange}
                  className="w-[300px]"
                  size="small"
                  showSearch
                  filterOption={(input, option) =>
                    selectFiltering(input, option)
                  }
                />
              </div>
            )}
          </>
        ) : (
          <>
            <div className="flex gap-x-2 items-center">
              <Title level={5} className="!w-[150px] !m-0 !text-start">
                Material Category
              </Title>
              {" - "}
              <Select
                loading={isLoadingCategories}
                value={selectedMaterialCategoryId}
                placeholder="Select Material Category"
                options={adaptedMaterialCategories}
                onChange={handleMaterialCategoryChange}
                className="w-[300px]"
                size="small"
                showSearch
                filterOption={(input, option) => selectFiltering(input, option)}
              />
            </div>

            {selectedMaterialCategoryId && (
              <div className="flex gap-x-2 items-center">
                <Title level={5} className="!w-[150px] !m-0 !text-start">
                  Material
                </Title>
                {" - "}
                <Select
                  loading={isLoadingMaterials}
                  value={values.material_id}
                  placeholder="Select Material"
                  options={adaptedMaterials}
                  onChange={handleMaterialChange}
                  className="w-[300px]"
                  size="small"
                  showSearch
                  filterOption={(input, option) =>
                    selectFiltering(input, option)
                  }
                />
              </div>
            )}
          </>
        )}

        {selectedMaterialCategoryId && dynamicFieldOptions.length > 0 && (
          <div className="flex gap-x-2 items-start">
            <Title level={5} className="!w-[150px] !m-0 !text-start">
              Dynamic Fields
            </Title>
            {" - "}
            <Select
              mode="multiple"
              value={values.dynamic_tracker_fields}
              placeholder="Select Dynamic Tracker Fields"
              options={dynamicFieldOptions}
              onChange={handleDynamicFieldsChange}
              className="w-[500px]"
              size="small"
              showSearch
              filterOption={(input, option) => selectFiltering(input, option)}
            />
          </div>
        )}
      </div>

      <Divider className="my-1.5" style={{ background: Colors.blue.primary }} />
    </>
  );
};
